import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import type { DonutChartProps } from './donut-chart.types';
import { buildOption, resolveColor, formatCompact } from './donut-chart.utils';

export function DonutChart({ labels, values, colors, redirectBase, redirectValues }: DonutChartProps) {
  const [disabledIndices, setDisabledIndices] = useState<Set<number>>(new Set());
  const [resolvedColors, setResolvedColors] = useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<ReactECharts>(null);

  const resolve = useCallback(
    () => setResolvedColors(colors.map(resolveColor)),
    [colors],
  );

  useEffect(() => {
    resolve();
    const observer = new MutationObserver(resolve);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, [resolve]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      chartRef.current?.getEchartsInstance().resize();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handleToggle = useCallback((index: number) => {
    setDisabledIndices(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        if (labels.length - next.size <= 1) return prev;
        next.add(index);
      }
      return next;
    });
  }, [labels.length]);

  const handleClick = useCallback((params: any) => {
    if (!redirectBase || !redirectValues) return;
    const active = redirectValues
      .map((val, i) => ({ val, i }))
      .filter(({ i }) => !disabledIndices.has(i));
    const clicked = active[params.dataIndex];
    if (clicked) {
      window.location.href = redirectBase + encodeURIComponent(clicked.val);
    }
  }, [redirectBase, redirectValues, disabledIndices]);

  const option = useMemo(() => {
    if (!resolvedColors.length) return {};
    return buildOption(labels, values, resolvedColors, disabledIndices);
  }, [labels, values, resolvedColors, disabledIndices]);

  const onEvents = useMemo(
    () => redirectBase ? { click: handleClick } : undefined,
    [redirectBase, handleClick],
  );

  if (!resolvedColors.length) return null;

  return (
    <div className="donut-chart" ref={containerRef}>
      <div className="donut-chart__canvas">
        <ReactECharts
          ref={chartRef}
          option={option}
          onEvents={onEvents}
          style={{ height: 280, width: '100%', cursor: redirectBase ? 'pointer' : 'default' }}
          opts={{ renderer: 'svg' }}
        />
      </div>
      <ul className="donut-legend">
        {labels.map((label, i) => (
          <li
            key={label}
            className={`donut-legend__item${disabledIndices.has(i) ? ' donut-legend__item--disabled' : ''}`}
            onClick={() => handleToggle(i)}
          >
            <span className="donut-legend__dot" style={{ backgroundColor: resolvedColors[i] }} />
            <span className="donut-legend__name">{label}</span>
            <span className="donut-legend__count">{formatCompact(values[i])}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
